import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ChevronLeft,
  ChevronRight,
  MapPin,
  CheckCircle2,
  GraduationCap,
  Sparkles,
  CalendarDays,
} from "lucide-react";
import { Button } from "@/components/ui/button";

// localStorage key — set once the photographer either finishes the tour OR
// dismisses it. Prevents the auto-popup on every login. The "Take the tour"
// footer button always re-opens regardless.
export const TUTORIAL_SEEN_KEY = "reelty.tutorial_seen_v1";

// Padding around the highlighted element for the spotlight cutout + ring.
const SPOTLIGHT_PADDING = 10;
// Gap between the highlighted element and the tutorial panel.
const PANEL_GAP = 20;
// Approximate panel dimensions used for placement math BEFORE render.
// The real panel measures itself after mount and we re-run placement — this
// is just the initial guess so the first paint isn't off-screen.
const PANEL_W_GUESS = 440;
const PANEL_H_GUESS = 380;

// Each step optionally targets a DOM element by CSS selector. When targeted:
//  1. we scroll it into view
//  2. cut a rectangular "hole" in the dark backdrop so the element shines through
//  3. add a gold ring around it
//  4. position the tutorial panel adjacent (auto-flipping if no space)
// When `target` is null → centered modal (used for the welcome step).
const STEPS = [
  {
    key: "welcome",
    icon: Sparkles,
    tag: "01 · WELCOME",
    title: "Here's how your onboarding works",
    body: "You'll set your service area, work through a short checklist, pass a Final Exam, and book your kickoff call — usually about 20 minutes end-to-end. Take this quick tour and I'll walk you through each section live on your dashboard.",
    accents: ["Set your area", "Check off docs", "Pass the exam", "Book the call"],
    target: null,
  },
  {
    key: "service_area",
    icon: MapPin,
    tag: "02 · SERVICE AREA",
    title: "Start here: your driving radius",
    body: (
      <>
        Type your <strong>base ZIP code</strong>, set how many miles you'll drive, and click{" "}
        <strong>Save service area</strong>. This is what tells Reelty where to route your
        shoots — you can update it anytime.
      </>
    ),
    target: '[data-testid="service-area-card"]',
    placement: "left",
  },
  {
    key: "checklist",
    icon: CheckCircle2,
    tag: "03 · CHECKLIST",
    title: "Marking documents complete",
    body: (
      <>
        For <strong>view-only docs</strong> — open the document via the primary button, then
        tap the circle on the left of the card to mark it done. For <strong>sign / upload
        items</strong>, the card shows an <strong>Upload</strong> button — attach your signed
        copy and the item auto-completes.
      </>
    ),
    target: '[data-testid="phase-documents"]',
    placement: "right",
  },
  {
    key: "final_exam",
    icon: GraduationCap,
    tag: "04 · FINAL EXAM",
    title: "The exam auto-checks on a passing grade",
    body: (
      <>
        The Final Exam stays <strong>locked</strong> until every Documents item is checked
        off. Once unlocked, pass with <strong>80%+</strong> and the exam item on your
        checklist <strong>auto-marks complete</strong> — you never have to check it yourself.
        Miss the mark? Retake it as many times as you need, no penalty.
      </>
    ),
    target: '[data-testid="phase-exam"]',
    placement: "right",
  },
  {
    key: "book_call",
    icon: CalendarDays,
    tag: "05 · FINISH LINE",
    title: "Book your kickoff call last",
    body: (
      <>
        Once every checklist item is checked <strong>and</strong> your service area is saved,
        the last step unlocks — a quick Calendly call with the Reelty team to hand you your
        first assignments. That's the finish line. You'll see a small celebration when you
        cross it 🎉
      </>
    ),
    target: '[data-testid="phase-scheduling"]',
    placement: "right",
  },
];

// Compute where the tutorial panel should sit given a target rect + a preferred
// placement. Auto-flips when there's no room, and falls back to a viewport
// corner if nothing fits. Returns { top, left } in fixed-positioning coords.
function computePanelPosition(rect, prefer, panelW, panelH) {
  if (!rect) {
    return null; // centered fallback handled by caller
  }
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const gap = PANEL_GAP;

  const fits = {
    right: rect.right + gap + panelW <= vw - 8,
    left: rect.left - gap - panelW >= 8,
    below: rect.bottom + gap + panelH <= vh - 8,
    above: rect.top - gap - panelH >= 8,
  };

  const order = ["right", "left", "below", "above"];
  const preferred = prefer && fits[prefer] ? prefer : order.find((p) => fits[p]);

  if (preferred === "right") {
    return {
      left: rect.right + gap,
      top: Math.max(8, Math.min(vh - panelH - 8, rect.top + rect.height / 2 - panelH / 2)),
    };
  }
  if (preferred === "left") {
    return {
      left: rect.left - gap - panelW,
      top: Math.max(8, Math.min(vh - panelH - 8, rect.top + rect.height / 2 - panelH / 2)),
    };
  }
  if (preferred === "below") {
    return {
      left: Math.max(8, Math.min(vw - panelW - 8, rect.left + rect.width / 2 - panelW / 2)),
      top: rect.bottom + gap,
    };
  }
  if (preferred === "above") {
    return {
      left: Math.max(8, Math.min(vw - panelW - 8, rect.left + rect.width / 2 - panelW / 2)),
      top: rect.top - gap - panelH,
    };
  }
  // Nothing fits — pin to bottom-right.
  return { left: vw - panelW - 16, top: vh - panelH - 16 };
}

export default function PhotographerTutorial({ open, onClose }) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null); // target's DOMRect (for spotlight)
  const [panelPos, setPanelPos] = useState(null); // { top, left } | null (=centered)
  // Mobile detection — the spotlight math (positioned panels adjacent to
  // near-full-width cards) fights the layout on small screens, so we fall
  // back to a plain centered modal there. Cards on mobile are stacked
  // full-width anyway; "highlighting" one adds no orientation value.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setIsMobile(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  const panelRef = useRef(null);
  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  // Reset step on re-open.
  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  // Body scroll lock while the tour is open — but only when there's NO target
  // (centered welcome step) OR we're on mobile (where the whole tour is
  // centered-modal mode). On desktop targeted steps we scroll the target into
  // view ourselves so we need the page to remain scrollable.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    if (!current.target || isMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = prev || "";
    }
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, current.target, isMobile]);

  // Measure the target + panel position. Skipped entirely on mobile — the
  // panel stays centered and we never dim/cut-out the backdrop.
  const measure = useCallback(() => {
    if (!open) return;
    if (isMobile || !current.target) {
      setRect(null);
      setPanelPos(null);
      return;
    }
    const el = document.querySelector(current.target);
    if (!el) {
      setRect(null);
      setPanelPos(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect(r);
    const pw = panelRef.current?.offsetWidth || PANEL_W_GUESS;
    const ph = panelRef.current?.offsetHeight || PANEL_H_GUESS;
    setPanelPos(computePanelPosition(r, current.placement, pw, ph));
  }, [open, current.target, current.placement, isMobile]);

  // On step change: scroll target into view, then measure. useLayoutEffect
  // to fire before paint so the spotlight & panel appear together. Mobile
  // path skips the scroll-into-view since the panel stays centered.
  useLayoutEffect(() => {
    if (!open) return;
    if (isMobile || !current.target) {
      measure();
      return;
    }
    const el = document.querySelector(current.target);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    }
    // Wait for scroll to settle before measuring. 350ms empirically covers a
    // smooth-scroll on both desktop and mobile.
    const t = setTimeout(measure, 380);
    return () => clearTimeout(t);
  }, [open, step, current.target, measure, isMobile]);

  // Keep the spotlight in sync while the user might scroll / resize.
  useEffect(() => {
    if (!open) return;
    const onChange = () => measure();
    window.addEventListener("scroll", onChange, { passive: true });
    window.addEventListener("resize", onChange);
    return () => {
      window.removeEventListener("scroll", onChange);
      window.removeEventListener("resize", onChange);
    };
  }, [open, measure]);

  const finish = () => {
    try {
      localStorage.setItem(TUTORIAL_SEEN_KEY, String(Date.now()));
    } catch {
      /* localStorage disabled — that's fine, they'll just see it again next login */
    }
    onClose?.();
  };

  const goto = (i) => {
    if (i < 0 || i >= STEPS.length) return;
    setStep(i);
  };

  // Spotlight rectangle (padded) used by both the SVG mask cutout and the
  // gold ring overlay. Null when no target → we render a plain dark backdrop.
  const spot = rect
    ? {
        x: rect.left - SPOTLIGHT_PADDING,
        y: rect.top - SPOTLIGHT_PADDING,
        w: rect.width + SPOTLIGHT_PADDING * 2,
        h: rect.height + SPOTLIGHT_PADDING * 2,
      }
    : null;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* BACKDROP with optional cutout. `pointer-events: none` so the user
              can still click their real dashboard elements — the tour is
              "live" alongside their actions, not blocking them. */}
          <motion.svg
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            data-testid="tutorial-backdrop"
            width="100%"
            height="100%"
            className="fixed inset-0 z-[199] pointer-events-none"
            style={{ position: "fixed" }}
          >
            <defs>
              <mask id="reelty-tutorial-mask">
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                {spot && (
                  <motion.rect
                    initial={false}
                    animate={{ x: spot.x, y: spot.y, width: spot.w, height: spot.h }}
                    transition={{ type: "spring", stiffness: 320, damping: 32 }}
                    rx="6"
                    fill="black"
                  />
                )}
              </mask>
            </defs>
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill="rgba(26,15,10,0.72)"
              mask="url(#reelty-tutorial-mask)"
            />
          </motion.svg>

          {/* Backdrop click-catcher — only over the DIMMED area, never over
              the cutout. Clicking anywhere dim closes; clicking the highlighted
              element passes through naturally. */}
          <div
            className="fixed inset-0 z-[199]"
            onClick={finish}
            data-testid="tutorial-backdrop-catcher"
            style={{
              // If there's a cutout, use clip-path to make the catcher skip
              // the highlighted rectangle so the user can interact with it.
              clipPath: spot
                ? `polygon(
                    0 0, 100% 0, 100% 100%, 0 100%, 0 0,
                    ${spot.x}px ${spot.y}px,
                    ${spot.x}px ${spot.y + spot.h}px,
                    ${spot.x + spot.w}px ${spot.y + spot.h}px,
                    ${spot.x + spot.w}px ${spot.y}px,
                    ${spot.x}px ${spot.y}px
                  )`
                : undefined,
            }}
          />

          {/* Gold ring around the target — pure visual, ignores pointer events. */}
          {spot && (
            <motion.div
              key="ring"
              initial={false}
              animate={{ x: spot.x, y: spot.y, width: spot.w, height: spot.h, opacity: 1 }}
              transition={{ type: "spring", stiffness: 320, damping: 32 }}
              data-testid="tutorial-ring"
              className="fixed top-0 left-0 z-[200] pointer-events-none rounded-md"
              style={{
                boxShadow:
                  "0 0 0 2px #B08D57, 0 0 0 5px rgba(176,141,87,0.35), 0 0 24px rgba(176,141,87,0.45)",
              }}
            />
          )}

          {/* PANEL — either adjacent to target (panelPos set) or centered. */}
          <motion.div
            key={`panel-${current.key}`}
            ref={panelRef}
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.24, ease: "easeOut" }}
            data-testid="tutorial-panel"
            className="fixed z-[201] w-[92vw] max-w-[440px] bg-[#FBF4E4] border border-[#1A0F0A]/12 rounded-sm shadow-[0_20px_60px_-20px_rgba(26,15,10,0.55)]"
            style={
              panelPos
                ? { top: panelPos.top, left: panelPos.left }
                : {
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                  }
            }
          >
            <button
              onClick={finish}
              data-testid="tutorial-close-btn"
              className="absolute top-3 right-3 p-1.5 text-[#78584B] hover:text-[#6B0F1A] hover:bg-[#6B0F1A]/8 rounded-sm transition-colors"
              aria-label="Close tour"
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </button>

            {/* Progress bar */}
            <div className="h-1 bg-[#1A0F0A]/8 rounded-t-sm overflow-hidden">
              <motion.div
                className="h-full bg-[#6B0F1A]"
                initial={false}
                animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
                transition={{ duration: 0.35, ease: "easeOut" }}
              />
            </div>

            <div className="p-6 pt-5">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-[#6B0F1A]/8 border border-[#6B0F1A]/15 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-[#6B0F1A]" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-mono-tag text-[10px] tracking-[0.25em] text-[#78584B]">
                    {current.tag}
                  </div>
                  <h2
                    className="font-display text-xl font-bold tracking-tight leading-tight text-[#1A0F0A] mt-1"
                    data-testid="tutorial-step-title"
                  >
                    {current.title}
                  </h2>
                </div>
              </div>

              <p
                className="text-[13px] text-[#4D3F35] leading-relaxed"
                data-testid="tutorial-step-body"
              >
                {current.body}
              </p>

              {current.accents && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {current.accents.map((a, i) => (
                    <span
                      key={a}
                      className="font-mono-tag text-[9px] tracking-wider px-2 py-1 rounded-sm bg-white border border-[#1A0F0A]/12 text-[#78584B]"
                    >
                      {String(i + 1).padStart(2, "0")} · {a.toUpperCase()}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#1A0F0A]/10">
                <div className="flex items-center gap-1.5" data-testid="tutorial-dots">
                  {STEPS.map((s, i) => (
                    <button
                      key={s.key}
                      onClick={() => goto(i)}
                      data-testid={`tutorial-dot-${i}`}
                      aria-label={`Go to step ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all ${
                        i === step
                          ? "bg-[#6B0F1A] w-4"
                          : i < step
                          ? "bg-[#6B0F1A]/40 w-1.5"
                          : "bg-[#1A0F0A]/15 w-1.5"
                      }`}
                    />
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  {step > 0 && (
                    <Button
                      variant="ghost"
                      onClick={() => goto(step - 1)}
                      data-testid="tutorial-prev-btn"
                      className="h-9 text-[#78584B] hover:bg-[#1A0F0A]/5 font-mono-tag text-[10px] tracking-wide"
                    >
                      <ChevronLeft className="w-3.5 h-3.5 mr-1" strokeWidth={2} />
                      BACK
                    </Button>
                  )}
                  {!isLast && (
                    <Button
                      onClick={() => goto(step + 1)}
                      data-testid="tutorial-next-btn"
                      className="h-9 bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] font-bold text-xs tracking-wide rounded-sm btn-primary-glow"
                    >
                      NEXT
                      <ChevronRight className="w-3.5 h-3.5 ml-1" strokeWidth={2} />
                    </Button>
                  )}
                  {isLast && (
                    <Button
                      onClick={finish}
                      data-testid="tutorial-finish-btn"
                      className="h-9 bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] font-bold text-xs tracking-wide rounded-sm btn-primary-glow"
                    >
                      <Sparkles className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
                      GOT IT — LET'S GO
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
